import {useEffect, useState} from 'react'

// Chrome/Edge fire this before showing their own install UI. Calling
// preventDefault() suppresses the native prompt so we can show a custom
// one instead, whenever we want, using the saved event.
interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>

    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function useInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [isInstalled, setIsInstalled] = useState(
        () => window.matchMedia('(display-mode: standalone)').matches,
    )

    useEffect(() => {
        const onBeforeInstallPrompt = (event: Event) => {
            event.preventDefault()
            setDeferredPrompt(event as BeforeInstallPromptEvent)
        }

        const onInstalled = () => {
            setIsInstalled(true)
            setDeferredPrompt(null)
        }

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        window.addEventListener('appinstalled', onInstalled)

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
            window.removeEventListener('appinstalled', onInstalled)
        }
    }, [])

    const promptInstall = async () => {
        if (!deferredPrompt) return

        await deferredPrompt.prompt()
        const {outcome} = await deferredPrompt.userChoice

        // The captured event can only be used once — clear it either way.
        setDeferredPrompt(null)
        return outcome
    }

    return {
        // true only on Chromium browsers that decided the PWA is installable
        // AND the user hasn't already installed or dismissed it this session.
        canInstall: deferredPrompt !== null,
        isInstalled,
        promptInstall,
    }
}
